/**
 * ChartControls Component
 * ========================
 * Controls and settings for the chart editor.
 */

import { useState, useEffect } from 'react';
import { useChartStore } from '@/stores/chartStore';
import { Card } from '@/components/ui/Card';

function ChartControls() {
  const { chart, setClinicalNotes, setAAPClassification, setRiskFactor, setReferral } = useChartStore();
  
  const canEdit = true; // TEMPORARY: Force editable for testing

  const [notes, setNotes] = useState(chart?.clinical_notes || '');
  const [aapStage, setAapStage] = useState(chart?.aap_stage || '');
  const [aapGrade, setAapGrade] = useState(chart?.aap_grade || '');
  const [aapExtent, setAapExtent] = useState(chart?.aap_extent || '');
  const [referralRecommended, setReferralRecommended] = useState(chart?.referral_recommended || false);
  const [referralReason, setReferralReason] = useState(chart?.referral_reason || '');

  // Update local state when chart changes
  useEffect(() => {
    setNotes(chart?.clinical_notes || '');
    setAapStage(chart?.aap_stage || '');
    setAapGrade(chart?.aap_grade || '');
    setAapExtent(chart?.aap_extent || '');
    setReferralRecommended(chart?.referral_recommended || false);
    setReferralReason(chart?.referral_reason || '');
  }, [chart]);

  const handleNotesBlur = () => {
    if (notes !== chart?.clinical_notes) {
      setClinicalNotes(notes);
    }
  };

  const handleStageChange = (value: string) => {
    setAapStage(value);
    setAAPClassification(value, aapGrade, aapExtent);
  };

  const handleGradeChange = (value: string) => {
    setAapGrade(value);
    setAAPClassification(aapStage, value, aapExtent);
  };

  const handleExtentChange = (value: string) => {
    setAapExtent(value);
    setAAPClassification(aapStage, aapGrade, value);
  };

  const handleReferralChange = (checked: boolean) => {
    setReferralRecommended(checked);
    setReferral(checked, checked ? referralReason : undefined);
  };

  const handleReferralReasonBlur = () => {
    if (referralRecommended && referralReason !== chart?.referral_reason) {
      setReferral(referralRecommended, referralReason);
    }
  };

  return (
    <div className="space-y-4">
      {/* Clinical Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Clinical Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          disabled={!canEdit}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          placeholder="Enter clinical observations..."
        />
      </div>

      {/* AAP Classification */}
      <Card className="p-4">
        <h3 className="font-medium text-gray-900 mb-3">AAP Classification</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Stage
            </label>
            <select
              value={aapStage}
              onChange={(e) => handleStageChange(e.target.value)}
              disabled={!canEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select Stage</option>
              <option value="Stage I">Stage I - Initial</option>
              <option value="Stage II">Stage II - Moderate</option>
              <option value="Stage III">Stage III - Severe (Potential for tooth loss)</option>
              <option value="Stage IV">Stage IV - Advanced (Tooth loss, masticatory dysfunction)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Grade
            </label>
            <select
              value={aapGrade}
              onChange={(e) => handleGradeChange(e.target.value)}
              disabled={!canEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select Grade</option>
              <option value="Grade A">Grade A - Slow rate of progression</option>
              <option value="Grade B">Grade B - Moderate rate</option>
              <option value="Grade C">Grade C - Rapid rate</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Extent
            </label>
            <select
              value={aapExtent}
              onChange={(e) => handleExtentChange(e.target.value)}
              disabled={!canEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select Extent</option>
              <option value="Localized">Localized (&lt;30% teeth)</option>
              <option value="Generalized">Generalized (≥30% teeth)</option>
              <option value="Molar/Incisor">Molar/Incisor pattern</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Risk Assessment */}
      <Card className="p-4">
        <h3 className="font-medium text-gray-900 mb-3">Risk Factors</h3>
        <div className="space-y-2">
          {[
            { id: 'smoking', label: 'Smoking/Tobacco Use' },
            { id: 'diabetes', label: 'Diabetes' },
            { id: 'genetics', label: 'Genetic Susceptibility' },
            { id: 'stress', label: 'Stress' },
            { id: 'medications', label: 'Medications Affecting Gingiva' },
            { id: 'poor_nutrition', label: 'Poor Nutrition' },
          ].map((factor) => (
            <label key={factor.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={chart?.risk_factors?.includes(factor.label) || false}
                disabled={!canEdit}
                onChange={(e) => {
                  setRiskFactor(factor.label, e.target.checked);
                }}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{factor.label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Referral */}
      <Card className="p-4">
        <h3 className="font-medium text-gray-900 mb-3">Referral</h3>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={referralRecommended}
            onChange={(e) => handleReferralChange(e.target.checked)}
            disabled={!canEdit}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Referral to Periodontist Recommended</span>
        </label>
        {referralRecommended && (
          <textarea
            placeholder="Referral reason..."
            value={referralReason}
            onChange={(e) => setReferralReason(e.target.value)}
            onBlur={handleReferralReasonBlur}
            disabled={!canEdit}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mt-2"
          />
        )}
      </Card>
    </div>
  );
}

export { ChartControls };
